<template>
  <vs-popup title="Subir Documentos" :active.sync="popupActive">
    <div class="p-8">
      <p class="mb-6 text-grey text-center">Selecciona un archivo ZIP con los documentos PDF a firmar.</p>
      
      <div 
        class="flex flex-col items-center justify-center border-2 border-dashed border-gray-400 rounded-xl p-12 cursor-pointer hover:bg-gray-100 hover:border-primary transition-all duration-300 ease-in-out bg-gray-50 mb-8"
        :class="{'border-primary bg-primary-light': selectedFiles.length > 0}"
        @click="triggerFileInput"
      >
        <feather-icon 
          :icon="selectedFiles.length > 0 ? 'CheckCircleIcon' : 'UploadCloudIcon'" 
          :svgClasses="['w-16 h-16 mb-4 transition-colors', selectedFiles.length > 0 ? 'text-primary' : 'text-grey']" 
        />
        <div v-if="selectedFiles.length === 0" class="text-center">
            <h5 class="mb-1">Haz clic para seleccionar</h5>
            <p class="text-grey text-sm">Solo archivos .ZIP son permitidos</p>
        </div>
        <div v-else class="text-center w-full px-4">
            <div v-for="(file, index) in selectedFiles" :key="index" class="mb-2 pb-2 border-b border-dashed border-gray-200 last:border-0">
              <h5 class="text-primary font-bold mb-0 text-sm truncate">{{ file.name }}</h5>
              <p class="text-grey text-xs font-medium">{{ (file.size / 1024 / 1024).toFixed(2) }} MB</p>
            </div>
        </div>
        <input type="file" ref="fileInput" class="hidden" accept=".zip" multiple @change="handleFileSelection">
      </div>

      <div class="flex justify-end gap-4 mt-4">
          <vs-button type="border" color="danger" class="px-8" @click="closePopup">Cancelar</vs-button>
          <vs-button @click="submitUpload" :disabled="selectedFiles.length === 0" class="px-8 shadow-md">Subir masivos</vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import { containerService } from '@/_services/containers.service';

export default {
  name: 'UploadContainerDocuments',
  props: {
    active: {
      type: Boolean,
      required: true
    },
    container: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      selectedFiles: []
    }
  },
  computed: {
    popupActive: {
      get() {
        return this.active
      },
      set(val) {
        if (!val) {
          this.closePopup()
        }
      }
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    handleFileSelection(event) {
      const files = Array.from(event.target.files)
      const validFiles = []
      
      files.forEach(file => {
        if (file.type === "application/x-zip-compressed" || file.type === "application/zip" || file.name.endsWith('.zip')) {
          validFiles.push(file)
        } else {
          this.$store.dispatch('alert/error', `El archivo ${file.name} no es un ZIP válido.`)
        }
      })

      if (validFiles.length > 0) {
        this.selectedFiles = [...this.selectedFiles, ...validFiles]
      }
    },
    async submitUpload() {
      if (this.selectedFiles.length === 0) return;

      this.$vs.loading()
      
      try {
        const uploadPromises = this.selectedFiles.map(async (file) => {
          // Fase 1: Inicialización
          const payload = {
            filename: file.name,
            contentType: "application/zip",
            size: file.size
          }
          const initResponse = await containerService.initMassiveUpload(this.container.uuid, payload);
          console.log(`Respuesta de inicialización para ${file.name}:`, initResponse);

          // Soporte para respuesta plana, envuelta en 'data' o un array dentro de 'data'
          let responseData = initResponse.data || initResponse;
          if (Array.isArray(responseData)) {
            responseData = responseData[0];
          }
          
          const { resumableSessionUrl, objectName } = responseData || {};

          if (responseData && resumableSessionUrl) {
            // Fase 2: Subida física
            console.log(`Iniciando subida física para ${file.name}...`);
            const uploadResponse = await fetch(resumableSessionUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/zip'
              },
              body: file
            });

            console.log(`Respuesta de subida física para ${file.name}:`, {
              status: uploadResponse.status,
              statusText: uploadResponse.statusText,
              ok: uploadResponse.ok
            });

            if (!uploadResponse.ok) {
              throw new Error(`Error en la subida física de ${file.name}: ${uploadResponse.statusText}`);
            }
            
            console.log(`Subida física finalizada para ${file.name}. Notificando completitud...`);
            
            // Fase 3: Completar carga masiva
            const completeResponse = await containerService.completeMassiveUpload(this.container.uuid, objectName);
            console.log(`Respuesta de completitud para ${file.name}:`, completeResponse);

            return completeResponse;
          } else {
            console.error(`Estructura de respuesta inesperada para ${file.name}:`, initResponse);
            throw new Error(`No se recibió resumableSessionUrl para ${file.name}. Revisa la consola para más detalles.`);
          }
        })

        await Promise.all(uploadPromises)
        
        this.$vs.loading.close()
        this.$store.dispatch('alert/success', `${this.selectedFiles.length} archivo(s) procesado(s) correctamente.`)
        
        this.selectedFiles = []
        this.$emit('upload-success')
        this.closePopup()
      } catch (error) {
        this.$vs.loading.close()
        this.$store.dispatch('alert/error', `Error en el proceso: ${error.message || error}`)
      }
    },
    closePopup() {
      this.selectedFiles = []
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/vuexy/_variables.scss";

.bg-primary-light {
  background-color: rgba($primary, 0.05) !important;
}
</style>

